import { useEffect, useRef, useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useAdminStore } from '../../store/adminStore'
import ImageCarousel from '../../components/ImageCarousel'
import Spinner from '../../components/common/Spinner'
import type { CrowdTaskItem } from '../../types'

const STATUS_COLORS: Record<string, { bg: string; color: string }> = {
  draft:         { bg: '#6b728022', color: '#9ca3af' },
  published:     { bg: '#22c55e22', color: '#22c55e' },
  fully_claimed: { bg: '#f59e0b22', color: '#f59e0b' },
  completed:     { bg: '#3b82f622', color: '#3b82f6' },
  expired:       { bg: '#ef444422', color: '#ef4444' },
  cancelled:     { bg: '#6b728022', color: '#9ca3af' },
  claimed:       { bg: '#6366f122', color: '#6366f1' },
  in_progress:   { bg: '#f59e0b22', color: '#f59e0b' },
  submitted:     { bg: '#3b82f622', color: '#3b82f6' },
  completed_a:   { bg: '#22c55e22', color: '#22c55e' },
  abandoned:     { bg: '#6b728022', color: '#9ca3af' },
}

const inputStyle = {
  background: 'var(--app-page-bg)',
  color: 'var(--tg-theme-text-color)',
  border: '1px solid transparent',
  borderRadius: '8px',
  padding: '6px 10px',
  width: '100%',
  fontSize: '14px',
  outline: 'none',
}

type ItemLang = 'ru' | 'en' | 'uz'

interface ItemDraft {
  id: string
  product_name: string
  product_description: string
  category_id: string
  translations: Partial<Record<'en' | 'uz', { product_name: string; product_description: string }>>
  activeLang: ItemLang
}

function buildTranslations(
  trans: Partial<Record<'en' | 'uz', { product_name: string; product_description: string }>>
) {
  const result: Record<string, { product_name?: string; product_description?: string }> = {}
  for (const [lang, t] of Object.entries(trans)) {
    if (t?.product_name?.trim() || t?.product_description?.trim()) {
      result[lang] = {
        product_name: t.product_name?.trim() || undefined,
        product_description: t.product_description?.trim() || undefined,
      }
    }
  }
  return Object.keys(result).length ? result : undefined
}

export default function AdminTaskDetailPage() {
  const { taskId } = useParams<{ taskId: string }>()
  const navigate = useNavigate()
  const {
    taskDetail,
    fetchTaskDetail,
    fetchCategories,
    publishTask,
    addTaskItem,
    updateTaskItem,
    deleteTaskItem,
    uploadTaskGalleryImage,
    deleteTaskGalleryImage,
    uploadTaskItemGalleryImage,
    deleteTaskItemGalleryImage,
    categories,
    isLoading,
  } = useAdminStore()

  const galleryInputRef = useRef<HTMLInputElement>(null)
  const itemGalleryInputRef = useRef<HTMLInputElement>(null)
  const [galleryUploading, setGalleryUploading] = useState(false)
  const [galleryError, setGalleryError] = useState<string | null>(null)
  const [pendingGalleryUrls, setPendingGalleryUrls] = useState<string[]>([])
  const [pendingItemUrls, setPendingItemUrls] = useState<Record<string, string[]>>({})
  const [uploadingImageForItemId, setUploadingImageForItemId] = useState<string | null>(null)
  const [editingItems, setEditingItems] = useState(false)
  const [itemDrafts, setItemDrafts] = useState<ItemDraft[]>([])
  const [newItem, setNewItem] = useState({ product_name: '', product_description: '', category_id: '', activeLang: 'ru' as ItemLang, translations: {} as Partial<Record<'en' | 'uz', { product_name: string; product_description: string }>> })
  const [itemsError, setItemsError] = useState<string | null>(null)
  const [savingItemId, setSavingItemId] = useState<string | null>(null)
  const [savedItemIds, setSavedItemIds] = useState<Set<string>>(new Set())
  const [addingItem, setAddingItem] = useState(false)

  useEffect(() => {
    if (taskId) {
      fetchTaskDetail(taskId)
      fetchCategories()
    }
  }, [taskId, fetchTaskDetail, fetchCategories])

  // Only block render on initial load (no data yet). Background refreshes keep showing existing content.
  if (!taskDetail || taskDetail.id !== taskId) return <Spinner />

  const task = taskDetail
  const expiresDate = new Date(task.expires_at)
  const isExpired = expiresDate < new Date()
  const taskStatusStyle = STATUS_COLORS[task.status] ?? STATUS_COLORS.draft

  const handlePublish = async () => {
    try {
      await publishTask(task.id)
      await fetchTaskDetail(task.id)
    } catch (e: unknown) {
      alert(e instanceof Error ? e.message : 'Failed to publish')
    }
  }

  const startEditingItems = () => {
    setItemDrafts(
      task.items.map((item: CrowdTaskItem) => {
        const trans: Partial<Record<'en' | 'uz', { product_name: string; product_description: string }>> = {}
        if (item.translations) {
          for (const lang of ['en', 'uz'] as const) {
            const t = item.translations[lang]
            if (t) trans[lang] = { product_name: t.product_name ?? '', product_description: t.product_description ?? '' }
          }
        }
        return {
          id: item.id,
          product_name: item.product_name,
          product_description: item.product_description || '',
          category_id: item.category_id || '',
          translations: trans,
          activeLang: 'ru' as ItemLang,
        }
      })
    )
    setNewItem({ product_name: '', product_description: '', category_id: '', activeLang: 'ru', translations: {} })
    setItemsError(null)
    setEditingItems(true)
  }

  const stopEditingItems = () => {
    setEditingItems(false)
    setItemDrafts([])
    setItemsError(null)
  }

  const handleSaveItem = async (draft: ItemDraft) => {
    setSavingItemId(draft.id)
    setItemsError(null)
    try {
      await updateTaskItem(task.id, draft.id, {
        product_name: draft.product_name.trim() || undefined,
        product_description: draft.product_description.trim() || undefined,
        category_id: draft.category_id || undefined,
        translations: buildTranslations(draft.translations),
      })
      setSavedItemIds((prev) => new Set([...prev, draft.id]))
      setTimeout(() => {
        setSavedItemIds((prev) => { const n = new Set(prev); n.delete(draft.id); return n })
      }, 2000)
    } catch (e: unknown) {
      setItemsError(e instanceof Error ? e.message : 'Failed to save item')
    } finally {
      setSavingItemId(null)
    }
  }

  const handleDeleteItem = async (itemId: string) => {
    if (!confirm('Delete this item?')) return
    setItemsError(null)
    try {
      await deleteTaskItem(task.id, itemId)
      setItemDrafts((prev) => prev.filter((d) => d.id !== itemId))
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : 'Failed to delete item'
      if (msg.toLowerCase().includes('submission')) {
        setItemsError('Item has submissions and cannot be deleted')
      } else {
        setItemsError(msg)
      }
    }
  }

  const handleAddItem = async () => {
    if (!newItem.product_name.trim()) return
    setAddingItem(true)
    setItemsError(null)
    try {
      await addTaskItem(task.id, {
        product_name: newItem.product_name.trim(),
        product_description: newItem.product_description.trim() || undefined,
        category_id: newItem.category_id || undefined,
        sort_order: task.items.length,
        translations: buildTranslations(newItem.translations),
      })
      // Sync drafts with updated task items
      const updatedTask = useAdminStore.getState().taskDetail
      if (updatedTask) {
        setItemDrafts(
          updatedTask.items.map((item: CrowdTaskItem) => {
            const trans: Partial<Record<'en' | 'uz', { product_name: string; product_description: string }>> = {}
            if (item.translations) {
              for (const lang of ['en', 'uz'] as const) {
                const t = item.translations[lang]
                if (t) trans[lang] = { product_name: t.product_name ?? '', product_description: t.product_description ?? '' }
              }
            }
            return {
              id: item.id,
              product_name: item.product_name,
              product_description: item.product_description || '',
              category_id: item.category_id || '',
              translations: trans,
              activeLang: 'ru' as ItemLang,
            }
          })
        )
      }
      setNewItem({ product_name: '', product_description: '', category_id: '', activeLang: 'ru', translations: {} })
    } catch (e: unknown) {
      setItemsError(e instanceof Error ? e.message : 'Failed to add item')
    } finally {
      setAddingItem(false)
    }
  }

  return (
    <div className="p-4 space-y-4 pb-8">
      <div className="flex items-center justify-between">
        <button
          className="text-sm opacity-60"
          style={{ color: 'var(--tg-theme-link-color)' }}
          onClick={() => navigate(-1)}
        >
          ← Back
        </button>
        <button
          className="text-xs px-3 py-1.5 rounded-lg font-medium"
          style={{ background: 'var(--app-card-bg)', color: 'var(--tg-theme-link-color)' }}
          onClick={() => navigate(`/admin/tasks/${task.id}/edit`)}
        >
          Edit
        </button>
      </div>

      {/* Image gallery */}
      {(() => {
        const serverImages = [
          ...(task.header_image_url ? [task.header_image_url] : []),
          ...(task.gallery_images ?? []).map((img) => img.image_url),
        ]
        const carouselImages = [...serverImages, ...pendingGalleryUrls]
        return (
          <div>
            {carouselImages.length > 0 && (
              <div className="rounded-xl overflow-hidden mb-2">
                <ImageCarousel images={carouselImages} height={180} showThumbs />
              </div>
            )}

            {/* Gallery management */}
            {galleryError && (
              <p className="text-xs mb-2 px-3 py-2 rounded-lg" style={{ background: '#ef444418', color: '#ef4444' }}>
                {galleryError}
              </p>
            )}
            <div className="flex gap-2 flex-wrap">
              {/* Existing gallery images with delete */}
              {(task.gallery_images ?? []).map((img) => (
                <div key={img.id} className="relative flex-shrink-0" style={{ width: 64, height: 64 }}>
                  <img src={img.image_url} alt="" className="rounded-lg w-full h-full object-cover" />
                  <button
                    className="absolute -top-1.5 -right-1.5 w-5 h-5 rounded-full flex items-center justify-center text-xs font-bold"
                    style={{ background: '#ef4444', color: 'white', lineHeight: 1 }}
                    onClick={async () => {
                      setGalleryError(null)
                      try {
                        await deleteTaskGalleryImage(task.id, img.id)
                        fetchTaskDetail(task.id)
                      } catch (e: unknown) {
                        setGalleryError(e instanceof Error ? e.message : 'Failed to delete image')
                      }
                    }}
                  >
                    ✕
                  </button>
                </div>
              ))}

              {/* Pending local previews (uploading) */}
              {pendingGalleryUrls.map((url) => (
                <div key={url} className="relative flex-shrink-0" style={{ width: 64, height: 64 }}>
                  <img src={url} alt="" className="rounded-lg w-full h-full object-cover" />
                  <div
                    className="absolute inset-0 rounded-lg flex items-center justify-center"
                    style={{ background: 'rgba(0,0,0,0.35)' }}
                  >
                    <span className="text-white text-xs font-medium">…</span>
                  </div>
                </div>
              ))}

              {/* Add image button */}
              <button
                className="rounded-lg flex flex-col items-center justify-center flex-shrink-0"
                style={{
                  width: 64, height: 64,
                  border: '2px dashed var(--tg-theme-hint-color)',
                  color: 'var(--tg-theme-hint-color)',
                  fontSize: 22,
                }}
                onClick={() => galleryInputRef.current?.click()}
              >
                +
              </button>
              <input
                ref={galleryInputRef}
                type="file"
                accept="image/jpeg,image/png,image/webp"
                className="hidden"
                onChange={async (e) => {
                  const file = e.target.files?.[0]
                  if (!file) return
                  e.target.value = ''
                  const localUrl = URL.createObjectURL(file)
                  setPendingGalleryUrls((prev) => [...prev, localUrl])
                  setGalleryError(null)
                  try {
                    await uploadTaskGalleryImage(task.id, file)
                    fetchTaskDetail(task.id)
                  } catch (err: unknown) {
                    setGalleryError(err instanceof Error ? err.message : 'Upload failed')
                  } finally {
                    setPendingGalleryUrls((prev) => prev.filter((u) => u !== localUrl))
                    URL.revokeObjectURL(localUrl)
                  }
                }}
              />
            </div>
          </div>
        )
      })()}

      {/* Task header */}
      <div className="rounded-xl p-4 space-y-2" style={{ background: 'var(--app-card-bg)' }}>
        <div className="flex items-start justify-between gap-2">
          <h1 className="text-base font-bold leading-tight" style={{ color: 'var(--tg-theme-text-color)' }}>
            {task.title}
          </h1>
          <span
            className="text-xs px-2 py-0.5 rounded-full font-medium flex-shrink-0"
            style={{ background: taskStatusStyle.bg, color: taskStatusStyle.color }}
          >
            {task.status.replace('_', ' ')}
          </span>
        </div>

        {task.description && (
          <p className="text-sm opacity-60" style={{ color: 'var(--tg-theme-text-color)' }}>
            {task.description}
          </p>
        )}

        <div className="grid grid-cols-2 gap-2 text-xs" style={{ color: 'var(--tg-theme-text-color)' }}>
          <div className="opacity-50">Type: <span className="opacity-100 font-medium uppercase">{task.task_type}</span></div>
          <div className="opacity-50">Claims: <span className="opacity-100 font-medium">{task.current_claims}/{task.max_claims}</span></div>
          {task.reward_per_item_coins && (
            <div className="opacity-50">Reward: <span className="opacity-100 font-medium">🪙 {task.reward_per_item_coins}/item</span></div>
          )}
          <div className={`opacity-50 ${isExpired ? 'text-red-400' : ''}`}>
            Expires: <span className={`font-medium ${isExpired ? 'text-red-400 opacity-100' : 'opacity-100'}`}>
              {expiresDate.toLocaleDateString()}
            </span>
          </div>
        </div>

        {task.target_location_name && (
          <p className="text-xs" style={{ color: 'var(--tg-theme-link-color)' }}>
            📍 {task.target_location_name}
          </p>
        )}

        {task.status === 'draft' && (
          <button
            className="w-full py-2 rounded-lg text-xs font-semibold mt-1"
            style={{ background: '#22c55e', color: 'white' }}
            onClick={handlePublish}
          >
            Publish →
          </button>
        )}
      </div>

      {/* Items */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <p className="text-xs font-medium uppercase opacity-40" style={{ color: 'var(--tg-theme-text-color)' }}>
            Items ({task.items.length})
          </p>
          <button
            className="text-xs px-2.5 py-1 rounded-lg font-medium"
            style={{
              background: editingItems ? 'var(--app-card-bg)' : 'var(--app-card-bg)',
              color: editingItems ? '#ef4444' : 'var(--tg-theme-link-color)',
            }}
            onClick={editingItems ? stopEditingItems : startEditingItems}
          >
            {editingItems ? 'Done' : 'Edit Items'}
          </button>
        </div>

        {itemsError && (
          <p className="text-xs mb-2 px-3 py-2 rounded-lg" style={{ background: '#ef444418', color: '#ef4444' }}>
            {itemsError}
          </p>
        )}

        {editingItems ? (
          <div className="space-y-2">
            {itemDrafts.map((draft) => {
              const currentItem = task.items.find((i) => i.id === draft.id)
              return (
                <div key={draft.id} className="rounded-xl p-3 space-y-2" style={{ background: 'var(--app-card-bg)' }}>
                  {/* Card header: name preview + compact lang tabs */}
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-xs opacity-40 truncate flex-1" style={{ color: 'var(--tg-theme-text-color)' }}>
                      {draft.product_name || 'Item'}
                    </span>
                    <div className="flex items-center gap-1 flex-shrink-0">
                      {(['ru', 'en', 'uz'] as ItemLang[]).map((lang) => {
                        const hasContent = lang !== 'ru' && (
                          draft.translations[lang as 'en' | 'uz']?.product_name?.trim() ||
                          draft.translations[lang as 'en' | 'uz']?.product_description?.trim()
                        )
                        return (
                          <button
                            key={lang}
                            className="px-2 py-0.5 rounded-md text-xs font-semibold uppercase relative"
                            style={{
                              background: draft.activeLang === lang ? 'var(--tg-theme-button-color)' : 'var(--app-page-bg)',
                              color: draft.activeLang === lang ? 'var(--tg-theme-button-text-color)' : 'var(--tg-theme-hint-color)',
                            }}
                            onClick={() => setItemDrafts((prev) => prev.map((d) => d.id === draft.id ? { ...d, activeLang: lang } : d))}
                          >
                            {lang}
                            {hasContent && draft.activeLang !== lang && (
                              <span className="absolute -top-0.5 -right-0.5 w-1.5 h-1.5 rounded-full bg-green-500" />
                            )}
                          </button>
                        )
                      })}
                    </div>
                  </div>
                  {/* Product name */}
                  <input
                    style={inputStyle}
                    placeholder={draft.activeLang === 'ru' ? 'Product name' : `Product name (${draft.activeLang.toUpperCase()}, optional)`}
                    value={draft.activeLang === 'ru' ? draft.product_name : (draft.translations[draft.activeLang as 'en' | 'uz']?.product_name ?? '')}
                    onChange={(e) =>
                      setItemDrafts((prev) =>
                        prev.map((d) => {
                          if (d.id !== draft.id) return d
                          if (d.activeLang === 'ru') return { ...d, product_name: e.target.value }
                          const lang = d.activeLang as 'en' | 'uz'
                          const prev_t = d.translations[lang] ?? { product_name: '', product_description: '' }
                          return { ...d, translations: { ...d.translations, [lang]: { ...prev_t, product_name: e.target.value } } }
                        })
                      )
                    }
                  />

                  {/* Item image gallery */}
                  <div className="flex gap-2 flex-wrap">
                    {(currentItem?.item_images ?? []).map((img) => (
                      <div key={img.id} className="relative flex-shrink-0" style={{ width: 56, height: 56 }}>
                        <img src={img.image_url} alt="" className="rounded-lg w-full h-full object-cover" />
                        <button
                          className="absolute -top-1.5 -right-1.5 w-5 h-5 rounded-full flex items-center justify-center text-xs font-bold"
                          style={{ background: '#ef4444', color: 'white', lineHeight: 1 }}
                          onClick={async () => {
                            setItemsError(null)
                            try {
                              await deleteTaskItemGalleryImage(task.id, draft.id, img.id)
                              fetchTaskDetail(task.id)
                            } catch (e: unknown) {
                              setItemsError(e instanceof Error ? e.message : 'Failed to delete image')
                            }
                          }}
                        >
                          ✕
                        </button>
                      </div>
                    ))}
                    {/* Pending local previews for this item */}
                    {(pendingItemUrls[draft.id] ?? []).map((url) => (
                      <div key={url} className="relative flex-shrink-0" style={{ width: 56, height: 56 }}>
                        <img src={url} alt="" className="rounded-lg w-full h-full object-cover" />
                        <div
                          className="absolute inset-0 rounded-lg flex items-center justify-center"
                          style={{ background: 'rgba(0,0,0,0.35)' }}
                        >
                          <span className="text-white text-xs font-medium">…</span>
                        </div>
                      </div>
                    ))}
                    <button
                      className="rounded-lg flex items-center justify-center flex-shrink-0"
                      style={{
                        width: 56, height: 56,
                        border: '2px dashed var(--tg-theme-hint-color)',
                        color: 'var(--tg-theme-hint-color)',
                        fontSize: 22,
                      }}
                      onClick={() => {
                        setUploadingImageForItemId(draft.id)
                        itemGalleryInputRef.current?.click()
                      }}
                    >
                      +
                    </button>
                  </div>
                  <input
                    style={inputStyle}
                    placeholder={draft.activeLang === 'ru' ? 'Description (optional)' : `Description (${draft.activeLang.toUpperCase()}, optional)`}
                    value={draft.activeLang === 'ru' ? draft.product_description : (draft.translations[draft.activeLang as 'en' | 'uz']?.product_description ?? '')}
                    onChange={(e) =>
                      setItemDrafts((prev) =>
                        prev.map((d) => {
                          if (d.id !== draft.id) return d
                          if (d.activeLang === 'ru') return { ...d, product_description: e.target.value }
                          const lang = d.activeLang as 'en' | 'uz'
                          const prev_t = d.translations[lang] ?? { product_name: '', product_description: '' }
                          return { ...d, translations: { ...d.translations, [lang]: { ...prev_t, product_description: e.target.value } } }
                        })
                      )
                    }
                  />
                  {categories.length > 0 && (
                    <select
                      style={{ ...inputStyle, cursor: 'pointer' }}
                      value={draft.category_id}
                      onChange={(e) =>
                        setItemDrafts((prev) =>
                          prev.map((d) => d.id === draft.id ? { ...d, category_id: e.target.value } : d)
                        )
                      }
                    >
                      <option value="">— Category —</option>
                      {categories.map((c) => (
                        <option key={c.id} value={c.id}>{c.name}</option>
                      ))}
                    </select>
                  )}
                  <div className="flex gap-2">
                    <button
                      className="flex-1 py-1.5 rounded-lg text-xs font-medium disabled:opacity-40"
                      style={{
                        background: savedItemIds.has(draft.id) ? '#22c55e' : 'var(--tg-theme-button-color)',
                        color: 'var(--tg-theme-button-text-color)',
                        transition: 'background 0.2s',
                      }}
                      disabled={savingItemId === draft.id || savedItemIds.has(draft.id)}
                      onClick={() => handleSaveItem(draft)}
                    >
                      {savingItemId === draft.id ? 'Saving…' : savedItemIds.has(draft.id) ? '✓ Saved' : 'Save'}
                    </button>
                    <button
                      className="px-3 py-1.5 rounded-lg text-xs font-medium"
                      style={{ background: '#ef444418', color: '#ef4444' }}
                      onClick={() => handleDeleteItem(draft.id)}
                    >
                      Delete
                    </button>
                  </div>
                </div>
              )
            })}
            {/* Hidden file input shared across item gallery image uploads */}
            <input
              ref={itemGalleryInputRef}
              type="file"
              accept="image/jpeg,image/png,image/webp"
              className="hidden"
              onChange={async (e) => {
                const file = e.target.files?.[0]
                e.target.value = ''
                if (!file || !uploadingImageForItemId) return
                const targetItemId = uploadingImageForItemId
                const localUrl = URL.createObjectURL(file)
                setPendingItemUrls((prev) => ({
                  ...prev,
                  [targetItemId]: [...(prev[targetItemId] ?? []), localUrl],
                }))
                setItemsError(null)
                try {
                  await uploadTaskItemGalleryImage(task.id, targetItemId, file)
                  fetchTaskDetail(task.id)
                } catch (err: unknown) {
                  setItemsError(err instanceof Error ? err.message : 'Image upload failed')
                } finally {
                  setUploadingImageForItemId(null)
                  setPendingItemUrls((prev) => ({
                    ...prev,
                    [targetItemId]: (prev[targetItemId] ?? []).filter((u) => u !== localUrl),
                  }))
                  URL.revokeObjectURL(localUrl)
                }
              }}
            />

            {/* Add new item */}
            <div className="rounded-xl p-3 space-y-2" style={{ background: 'var(--app-card-bg)', border: '1px dashed var(--tg-theme-hint-color)' }}>
              <div className="flex items-center justify-between gap-2">
                <p className="text-xs opacity-50" style={{ color: 'var(--tg-theme-text-color)' }}>Add item</p>
                <div className="flex items-center gap-1">
                  {(['ru', 'en', 'uz'] as ItemLang[]).map((lang) => {
                    const hasContent = lang !== 'ru' && (
                      newItem.translations[lang as 'en' | 'uz']?.product_name?.trim() ||
                      newItem.translations[lang as 'en' | 'uz']?.product_description?.trim()
                    )
                    return (
                      <button
                        key={lang}
                        className="px-2 py-0.5 rounded-md text-xs font-semibold uppercase relative"
                        style={{
                          background: newItem.activeLang === lang ? 'var(--tg-theme-button-color)' : 'var(--app-page-bg)',
                          color: newItem.activeLang === lang ? 'var(--tg-theme-button-text-color)' : 'var(--tg-theme-hint-color)',
                        }}
                        onClick={() => setNewItem((p) => ({ ...p, activeLang: lang }))}
                      >
                        {lang}
                        {hasContent && newItem.activeLang !== lang && (
                          <span className="absolute -top-0.5 -right-0.5 w-1.5 h-1.5 rounded-full bg-green-500" />
                        )}
                      </button>
                    )
                  })}
                </div>
              </div>
              <input
                style={inputStyle}
                placeholder={newItem.activeLang === 'ru' ? 'Product name *' : `Product name (${newItem.activeLang.toUpperCase()}, optional)`}
                value={newItem.activeLang === 'ru' ? newItem.product_name : (newItem.translations[newItem.activeLang as 'en' | 'uz']?.product_name ?? '')}
                onChange={(e) => {
                  if (newItem.activeLang === 'ru') {
                    setNewItem((p) => ({ ...p, product_name: e.target.value }))
                  } else {
                    const lang = newItem.activeLang as 'en' | 'uz'
                    setNewItem((p) => {
                      const prev_t = p.translations[lang] ?? { product_name: '', product_description: '' }
                      return { ...p, translations: { ...p.translations, [lang]: { ...prev_t, product_name: e.target.value } } }
                    })
                  }
                }}
              />
              <input
                style={inputStyle}
                placeholder={newItem.activeLang === 'ru' ? 'Description (optional)' : `Description (${newItem.activeLang.toUpperCase()}, optional)`}
                value={newItem.activeLang === 'ru' ? newItem.product_description : (newItem.translations[newItem.activeLang as 'en' | 'uz']?.product_description ?? '')}
                onChange={(e) => {
                  if (newItem.activeLang === 'ru') {
                    setNewItem((p) => ({ ...p, product_description: e.target.value }))
                  } else {
                    const lang = newItem.activeLang as 'en' | 'uz'
                    setNewItem((p) => {
                      const prev_t = p.translations[lang] ?? { product_name: '', product_description: '' }
                      return { ...p, translations: { ...p.translations, [lang]: { ...prev_t, product_description: e.target.value } } }
                    })
                  }
                }}
              />
              {categories.length > 0 && (
                <select
                  style={{ ...inputStyle, cursor: 'pointer' }}
                  value={newItem.category_id}
                  onChange={(e) => setNewItem((p) => ({ ...p, category_id: e.target.value }))}
                >
                  <option value="">— Category —</option>
                  {categories.map((c) => (
                    <option key={c.id} value={c.id}>{c.name}</option>
                  ))}
                </select>
              )}
              <button
                className="w-full py-1.5 rounded-lg text-xs font-semibold disabled:opacity-40"
                style={{ background: '#22c55e', color: 'white' }}
                disabled={addingItem || !newItem.product_name.trim()}
                onClick={handleAddItem}
              >
                {addingItem ? 'Adding…' : '+ Add'}
              </button>
            </div>
          </div>
        ) : (
          task.items.length > 0 ? (
            <div className="space-y-1.5">
              {task.items
                .sort((a, b) => a.sort_order - b.sort_order)
                .map((item) => {
                  const thumb = item.item_images?.[0]?.image_url ?? item.item_image_url
                  return (
                    <div
                      key={item.id}
                      className="flex items-center gap-2.5 rounded-xl px-3 py-2"
                      style={{ background: 'var(--app-card-bg)' }}
                    >
                      {/* Thumbnail */}
                      {thumb ? (
                        <img
                          src={thumb}
                          alt=""
                          className="rounded-lg flex-shrink-0 object-cover"
                          style={{ width: 40, height: 40 }}
                        />
                      ) : (
                        <div
                          className="rounded-lg flex-shrink-0 flex items-center justify-center text-xs opacity-20"
                          style={{ width: 40, height: 40, background: 'var(--app-page-bg)', color: 'var(--tg-theme-text-color)' }}
                        >
                          ?
                        </div>
                      )}
                      <span className="text-xs opacity-30 font-medium w-5 shrink-0" style={{ color: 'var(--tg-theme-text-color)' }}>
                        #{item.sort_order + 1}
                      </span>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium truncate" style={{ color: 'var(--tg-theme-text-color)' }}>
                          {item.product_name}
                        </p>
                        {item.product_description && (
                          <p className="text-xs opacity-50 truncate" style={{ color: 'var(--tg-theme-text-color)' }}>
                            {item.product_description}
                          </p>
                        )}
                      </div>
                    </div>
                  )
                })}
            </div>
          ) : (
            <p className="text-sm text-center opacity-40 py-4" style={{ color: 'var(--tg-theme-text-color)' }}>
              No items — click "Edit Items" to add some
            </p>
          )
        )}
      </div>

      {/* Assignments */}
      <div>
        <p className="text-xs font-medium uppercase opacity-40 mb-2" style={{ color: 'var(--tg-theme-text-color)' }}>
          Assignments ({task.assignments.length})
        </p>
        {task.assignments.length === 0 ? (
          <p className="text-sm text-center opacity-40 py-4" style={{ color: 'var(--tg-theme-text-color)' }}>
            No assignments yet
          </p>
        ) : (
          <div className="space-y-2">
            {task.assignments.map((a) => {
              const statusKey = a.status === 'completed' ? 'completed_a' : a.status
              const s = STATUS_COLORS[statusKey] ?? STATUS_COLORS.draft
              return (
                <div
                  key={a.id}
                  className="rounded-xl p-3"
                  style={{ background: 'var(--app-card-bg)' }}
                >
                  <div className="flex items-center justify-between gap-2">
                    <div>
                      <p className="text-sm font-medium" style={{ color: 'var(--tg-theme-text-color)' }}>
                        @{a.worker_username || a.worker_id.slice(0, 8)}
                      </p>
                      {a.claimed_at && (
                        <p className="text-xs opacity-50" style={{ color: 'var(--tg-theme-text-color)' }}>
                          Claimed {new Date(a.claimed_at).toLocaleDateString()}
                          {a.completed_at && ` · Done ${new Date(a.completed_at).toLocaleDateString()}`}
                        </p>
                      )}
                    </div>
                    <div className="text-right flex-shrink-0 space-y-1">
                      <span
                        className="text-xs px-2 py-0.5 rounded-full font-medium block"
                        style={{ background: s.bg, color: s.color }}
                      >
                        {a.status.replace('_', ' ')}
                      </span>
                      {(a.items_found > 0 || a.items_not_found > 0) && (
                        <p className="text-xs opacity-50" style={{ color: 'var(--tg-theme-text-color)' }}>
                          ✓ {a.items_found} · ✗ {a.items_not_found}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              )
            })}
          </div>
        )}
      </div>
    </div>
  )
}
